/**
 * Donor control recipes — the ONE spelling of each field and choice control in
 * the donor redesign (docs/initiatives/new/donor-experience-redesign/SPEC.md §C,
 * §E16–E19, E28, §F1; Paper boards 66D-0 "Controls & states" and 30D-0
 * "Foundations"). CON-515 (fields) and CON-516 (segmented, stepper, tiles).
 *
 * Every donor app (and the dashboard's form previewer) scans `shared/src` with
 * Tailwind, so these classes are emitted wherever they are imported. Tailwind
 * finds a class by reading this file's TEXT, so every CLASS below is written
 * out whole. Strings are composed only from whole classes (a shared base plus a
 * per-state suffix) — never from partial class names, which Tailwind cannot see.
 *
 * WHY THE BRAND READS CARRY A FALLBACK. The brand roles (--cs-brand-action,
 * -accent, -tint, -foreground) are written by the provider on a scope element,
 * never on :root (D4/D6), so a value file cannot declare a dial that points at
 * them: its var() would resolve at :root, where no brand exists. The no-brand
 * value therefore travels with each read, and it is SPEC §A6's: action and
 * accent fall back to the ink, tint to the page ground, foreground to white.
 *
 * Values never appear here — only token reads. Within one returned string no
 * two classes set the same property, because two Tailwind utilities for the
 * same property do not reliably resolve by their order in a class attribute.
 */

/* ── Focus and disabled ─────────────────────────────────────────────────── */

/** Every non-field control: 2px accent outline, offset 2px (SPEC §C Focus). */
export const FOCUS_OUTLINE_CLASS =
  'focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]';

/** Whole control at 60% when disabled, nothing else (SPEC §C Disabled). */
export const DISABLED_CLASS =
  'disabled:cursor-not-allowed disabled:opacity-[var(--cs-opacity-disabled)]';

const FOCUS_AND_DISABLED = `${FOCUS_OUTLINE_CLASS} ${DISABLED_CLASS}`;

/* ── Fields: input, select, textarea ────────────────────────────────────── */

/**
 * The field surface: 12 radius, white fill always, flat. Put it on the element
 * that draws the border — the input itself, or the wrapper around a bare input.
 * `--cs-control-shadow` resolves to none in the redesign; it is still read so
 * the conformance sentinels keep proving the field layer reads its dials.
 * Only the shadow transitions: animating the fill would hand the sentinels an
 * in-flight background value (and a field's fill never changes anyway).
 */
export const FIELD_SURFACE_CLASS =
  'box-border rounded-[var(--cs-input-radius)] border bg-[color:var(--cs-surface-0)] shadow-[shadow:var(--cs-control-shadow)] transition-shadow duration-[var(--cs-motion-instant)]';

/** Rest/hover edge, or the error edge (which does not react to hover). */
export function fieldBorderClass(hasError) {
  return hasError
    ? 'border-[color:var(--cs-error-color)]'
    : 'border-[color:var(--cs-border-control)] hover:border-[color:var(--cs-border-control-hover)]';
}

/** Focus on the field itself: accent border + 1px inset accent (reads as 2px, no shift). */
export const FIELD_FOCUS_CLASS =
  'outline-none focus:border-[color:var(--cs-input-focus-ring)] focus:shadow-[shadow:var(--cs-input-focus-shadow)]';

/** The same focus drawn by a wrapper whose bare input sits inside it. */
export const FIELD_FOCUS_WITHIN_CLASS =
  'focus-within:border-[color:var(--cs-input-focus-ring)] focus-within:shadow-[shadow:var(--cs-input-focus-shadow)]';

/** Value and placeholder: 16/24 regular (never below 16 — iOS zoom floor). */
export const FIELD_TEXT_CLASS =
  'font-sans text-[length:var(--cs-input-font-size)] leading-[var(--cs-leading-24)] font-normal text-[color:var(--cs-text-primary)] placeholder:text-[color:var(--cs-text-tertiary)]';

/** 48 tall, 16 inline padding. */
export const FIELD_BOX_CLASS = 'h-[var(--cs-input-height)] px-[var(--cs-input-padding-x)]';

/** Textarea: 96 minimum height (SPEC §C Inputs). */
export const TEXTAREA_MIN_HEIGHT_CLASS = 'min-h-24';

/** Select chevron: 20, tertiary ink. */
export const SELECT_CHEVRON_CLASS =
  'pointer-events-none h-[var(--cs-icon-lg)] w-[var(--cs-icon-lg)] text-[color:var(--cs-text-tertiary)]';

/** Where a field's select chevron sits: on the field's own inline inset, vertically centred, so
 *  every donor select (native or react-select) puts it at the same place. */
export const SELECT_CHEVRON_FIELD_CLASS =
  'absolute right-[var(--cs-input-padding-x)] top-1/2 z-10 -translate-y-1/2';

/** Icon button inside a title strip, sheet or modal (SPEC §C Icon buttons, CON-514 / CON-517):
 *  36, radius 12, transparent, the accent focus outline. The strip itself is surface-subtle, so
 *  a surface-subtle hover was invisible there: hover and press take the sunken surface and the
 *  glyph darkens to primary ink. The drawer's close and a form step's back both use it. */
export const STRIP_ICON_BUTTON_CLASS =
  'group flex flex-shrink-0 items-center justify-center w-[var(--cs-size-button-sm)] h-[var(--cs-size-button-sm)] ' +
  'rounded-[var(--cs-control-radius)] bg-transparent transition-colors duration-[var(--cs-motion-instant)] ' +
  'hover:bg-[color:var(--cs-surface-sunken)] active:bg-[color:var(--cs-surface-sunken)] ' +
  'focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]';

/** Its glyph: 20, secondary ink. */
export const STRIP_ICON_CLASS =
  'w-[var(--cs-icon-lg)] h-[var(--cs-icon-lg)] text-[color:var(--cs-text-secondary)] ' +
  'transition-colors duration-[var(--cs-motion-instant)] group-hover:text-[color:var(--cs-text-primary)]';

/** A complete standalone field (input/select/textarea that draws its own border). */
export function fieldClass(hasError) {
  return `w-full ${FIELD_SURFACE_CLASS} ${fieldBorderClass(hasError)} ${FIELD_TEXT_CLASS} ${FIELD_FOCUS_CLASS} ${DISABLED_CLASS}`;
}

/** Field label: 14/20 medium, secondary ink (SPEC §C Type). */
export const FIELD_LABEL_CLASS =
  'font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium text-[color:var(--cs-text-secondary)]';

/** The required asterisk: its own node, error colour (SPEC §E25). */
export const REQUIRED_MARK_CLASS = 'text-[color:var(--cs-error-color)]';

/** Helper / error message line: 13/18 (error adds the error ink and a 14 icon). */
export const FIELD_MESSAGE_CLASS =
  'font-sans text-[length:var(--cs-text-xs-plus)] leading-[var(--cs-leading-18)] font-medium';

/* ── Checkbox and radio ─────────────────────────────────────────────────── */

const MARK_BASE =
  'relative inline-flex shrink-0 items-center justify-center box-border h-[var(--cs-size-checkbox)] w-[var(--cs-size-checkbox)] border';
const MARK_ON =
  'border-[color:var(--cs-brand-accent,var(--cs-text-primary))] bg-[color:var(--cs-brand-action,var(--cs-text-primary))] text-[color:var(--cs-brand-foreground,var(--cs-surface-0))]';
const MARK_OFF_FILL = 'bg-[color:var(--cs-surface-0)] hover:bg-[color:var(--cs-surface-1)] active:bg-[color:var(--cs-surface-sunken)]';

/**
 * Checkbox box: 18, radius 6. Off = white + strong edge (the error edge when the
 * field is invalid, Paper 66D-0 Checkbox → Error); on = brand action fill with
 * an accent edge and a brand-foreground tick (stroke 2.5). Keyed on `checked`
 * rather than data-state so it works for native inputs and Radix alike.
 */
export function checkboxClass(checked, hasError = false) {
  const state = checked
    ? MARK_ON
    : `${hasError ? 'border-[color:var(--cs-error-color)]' : 'border-[color:var(--cs-border-control-hover)]'} ${MARK_OFF_FILL}`;
  return `${MARK_BASE} rounded-[var(--cs-checkbox-radius)] ${state} ${FOCUS_AND_DISABLED}`;
}

/** The tick inside a checked box: 14, stroke 2.5 (SPEC §B — the one non-1.75 stroke). */
export const CHECKBOX_TICK_CLASS = 'h-[var(--cs-icon-sm)] w-[var(--cs-icon-sm)]';
export const CHECKBOX_TICK_STROKE = 2.5;

/** Checkbox / consent label: 14/20 (SPEC §C Type). */
export const CHECKBOX_LABEL_CLASS =
  'font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-normal text-[color:var(--cs-text-primary)]';

/** Radio: 18 pill. Off = white + strong edge; on = brand action fill + 6px foreground dot (SPEC §E17). */
export function radioClass(checked) {
  const state = checked ? MARK_ON : `border-[color:var(--cs-border-control-hover)] ${MARK_OFF_FILL}`;
  return `${MARK_BASE} rounded-[var(--cs-pill-radius)] ${state} ${FOCUS_AND_DISABLED}`;
}

/** The radio's centre dot (6px, brand foreground). */
export const RADIO_DOT_CLASS =
  'block h-1.5 w-1.5 rounded-[var(--cs-pill-radius)] bg-[color:var(--cs-brand-foreground,var(--cs-surface-0))]';

/* ── Toggle ─────────────────────────────────────────────────────────────── */

/** Toggle track: 36×20 pill; off = sunken, on = brand action (SPEC §E18; literal sizes allowed). */
export function toggleTrackClass(on) {
  const fill = on
    ? 'bg-[color:var(--cs-brand-action,var(--cs-text-primary))]'
    : 'bg-[color:var(--cs-surface-sunken)]';
  return `relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center box-border rounded-[var(--cs-pill-radius)] p-0.5 ${fill} transition-colors duration-[var(--cs-motion-instant)] ${FOCUS_AND_DISABLED}`;
}

/** Toggle knob: 16 white disc with the CARD shadow. */
export function toggleKnobClass(on) {
  return `pointer-events-none inline-block h-4 w-4 ${on ? 'translate-x-4' : 'translate-x-0'} rounded-[var(--cs-pill-radius)] bg-[color:var(--cs-surface-0)] shadow-[shadow:var(--cs-card-shadow)] transition-transform duration-[var(--cs-motion-instant)]`;
}

/* ── Segmented control (frequency, item tabs, portal filters) ───────────── */

/**
 * Track: 40 tall, 4 inset, sunken fill, radius 12. An error draws a 1px error
 * edge as an inset OUTLINE, not a border, so the 32 pill still fits the track.
 * `flush`: the track fills the top of a card that clips it, so it takes no radius
 * of its own and the card's edge shapes its corners (a radius of its own leaves
 * a white sliver at each corner, and rounds the bottom above a straight divider).
 */
export function segmentTrackClass(hasError, { flush = false } = {}) {
  const radius = flush ? 'rounded-none' : 'rounded-[var(--cs-control-radius)]';
  return `isolate flex w-full box-border gap-[var(--cs-space-hairline)] h-[var(--cs-size-segment-track)] p-[var(--cs-space-1)] ${radius} bg-[color:var(--cs-surface-sunken)]${
    hasError ? ' outline -outline-offset-1 outline-[color:var(--cs-error-color)]' : ''
  }`;
}

const SEGMENT_BASE =
  'relative flex min-w-0 flex-1 items-center justify-center box-border h-[var(--cs-size-segment)] px-[var(--cs-space-3)] rounded-[var(--cs-segment-radius)] font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium select-none transition duration-[var(--cs-motion-instant)] focus-visible:z-10';

/**
 * One segment: 32 tall, radius 8, 14/20 medium. Selected = white pill with the
 * CARD shadow and no border; unselected = transparent, secondary ink that goes
 * to the ink on hover/press with no fill change (SPEC §E28).
 */
export function segmentClass(selected) {
  const state = selected
    ? 'bg-[color:var(--cs-surface-0)] shadow-[shadow:var(--cs-card-shadow)] text-[color:var(--cs-text-primary)] cursor-default'
    : 'bg-transparent text-[color:var(--cs-text-secondary)] hover:text-[color:var(--cs-text-primary)] active:text-[color:var(--cs-text-primary)] cursor-pointer';
  return `${SEGMENT_BASE} ${state} ${FOCUS_AND_DISABLED}`;
}

/* ── Banner chip (the host's top banner) ────────────────────────────────── */

/**
 * The white chip that sits on the host's coloured top banner: the Add to Cart
 * bar's Cart chip and the portal widget's Sign In chip. 32 tall, control
 * radius, 14/20 semibold ink, 8 between the leading mark and the label.
 */
export const BANNER_CHIP_CLASS =
  'relative flex h-[var(--cs-size-banner-chip)] shrink-0 flex-row items-center gap-[var(--cs-space-2)] overflow-hidden whitespace-nowrap rounded-[var(--cs-control-radius)] border-0 bg-[color:var(--cs-surface-0)] px-[var(--cs-space-3)] font-sans text-[length:var(--cs-text-sm)] font-semibold leading-[var(--cs-leading-20)] text-[color:var(--cs-text-primary)] transition-opacity duration-[var(--cs-motion-base)] hover:bg-[color:var(--cs-surface-1)] active:bg-[color:var(--cs-surface-sunken)] focus-visible:[outline:var(--cs-focus-outline)] focus-visible:outline-offset-0 disabled:cursor-default disabled:opacity-[var(--cs-opacity-disabled)]';

/* ── Underline tabs (portal page navigation) ────────────────────────────── */

/** The tab row: bottom 1px subtle edge. */
export const UNDERLINE_TAB_ROW_CLASS =
  'flex items-end gap-[var(--cs-space-6)] border-b border-[color:var(--cs-border-control-subtle)]';

/** One tab: 14/20 medium; selected = ink + 2px accent underline (SPEC §E16, §F1). */
export function underlineTabClass(selected) {
  const state = selected
    ? 'border-[color:var(--cs-brand-accent,var(--cs-text-primary))] text-[color:var(--cs-text-primary)]'
    : 'border-transparent text-[color:var(--cs-text-secondary)] hover:text-[color:var(--cs-text-primary)]';
  return `relative -mb-px box-border border-b-[length:var(--cs-border-width-selected)] pb-[var(--cs-space-3)] font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium ${state} ${FOCUS_OUTLINE_CLASS}`;
}

/* ── Selection tiles and chips (amounts, giving levels, items) ──────────── */

/**
 * A choice tile / amount chip. Rest = white, 1px edge, no shadow (the control
 * shadow dial resolves to none), regular weight; hover = strong edge + subtle
 * fill; pressed = sunken. Selected = 2px accent edge on the brand tint, medium
 * weight, no hover change (Paper 66D-0 "Amount tile"). Height and padding
 * belong to the call site (a 48 chip vs a giving-level card).
 */
/**
 * A selected choice tile's edge and fill: the brand's selected roles (the Give button's colour
 * and 12% of it, brandTokens.js rule 7), falling back to the accent and tint, then to neutrals.
 * Every selected tile (amounts, giving levels, sponsorship groups, ticket rows) reads this.
 */
export const TILE_SELECTED_COLOR_CLASS =
  'border-[length:var(--cs-border-width-selected)] border-[color:var(--cs-brand-selected,var(--cs-brand-accent,var(--cs-text-primary)))] bg-[color:var(--cs-brand-selected-tint,var(--cs-brand-tint,var(--cs-surface-1)))]';

export function tileClass(selected) {
  const state = selected
    ? `${TILE_SELECTED_COLOR_CLASS} font-medium cursor-default`
    : 'border border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)] shadow-[shadow:var(--cs-control-shadow)] font-normal cursor-pointer hover:border-[color:var(--cs-border-control-hover)] hover:bg-[color:var(--cs-surface-1)] active:bg-[color:var(--cs-surface-sunken)] disabled:hover:border-[color:var(--cs-border-control)] disabled:hover:bg-[color:var(--cs-surface-0)]';
  return `box-border rounded-[var(--cs-control-radius)] text-[color:var(--cs-text-primary)] select-none transition-colors duration-[var(--cs-motion-instant)] ${state} ${FOCUS_AND_DISABLED}`;
}

/** Amount chip label: 15/22 (weight comes from tileClass — regular at rest, medium selected). */
export const TILE_LABEL_CLASS =
  'font-sans text-[length:var(--cs-text-sm-plus)] leading-[var(--cs-leading-22)]';

/* ── Stepper (tickets, quantities) ──────────────────────────────────────── */

/**
 * Stepper button: 30 on touch widths, 24 from `sm`, pill. Minus = white + 1px
 * edge + secondary glyph; plus = brand action + brand-foreground glyph. At
 * min/max the button is disabled (60%) (SPEC §C Stepper, §E11).
 */
export function stepperButtonClass(kind) {
  const state =
    kind === 'plus'
      ? 'bg-[color:var(--cs-brand-action,var(--cs-text-primary))] text-[color:var(--cs-brand-foreground,var(--cs-surface-0))] hover:bg-[color:var(--cs-brand-action-hover,var(--cs-text-primary))] active:bg-[color:var(--cs-brand-action-pressed,var(--cs-text-primary))] disabled:hover:bg-[color:var(--cs-brand-action,var(--cs-text-primary))]'
      : 'border border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)] text-[color:var(--cs-text-secondary)] hover:border-[color:var(--cs-border-control-hover)] hover:bg-[color:var(--cs-surface-1)] active:bg-[color:var(--cs-surface-sunken)] disabled:hover:border-[color:var(--cs-border-control)] disabled:hover:bg-[color:var(--cs-surface-0)]';
  return `inline-flex shrink-0 items-center justify-center box-border h-[var(--cs-size-stepper-touch)] w-[var(--cs-size-stepper-touch)] sm:h-[var(--cs-size-stepper)] sm:w-[var(--cs-size-stepper)] rounded-[var(--cs-pill-radius)] transition-colors duration-[var(--cs-motion-instant)] ${state} ${FOCUS_AND_DISABLED}`;
}

/** Stepper glyph: 14, stroke 1.75. */
export const STEPPER_ICON_CLASS = 'h-[var(--cs-icon-sm)] w-[var(--cs-icon-sm)]';

/** Stepper count: 16/24 semibold in a 20 slot. */
export const STEPPER_COUNT_CLASS =
  'inline-block w-5 text-center font-sans text-[length:var(--cs-text-base)] leading-[var(--cs-leading-24)] font-semibold tabular-nums text-[color:var(--cs-text-primary)]';

/** Gap between stepper parts. */
export const STEPPER_GROUP_CLASS = 'inline-flex items-center gap-[var(--cs-space-2)]';

/* ── Calendar day cell ──────────────────────────────────────────────────── */

/** Day cell: 36, radius 12. Selected = brand action + foreground with accent edge; today = strong edge (SPEC §E19, §F1). */
export function calendarDayClass({ selected = false, today = false, strikeDisabled = true } = {}) {
  let state;
  if (selected) {
    state =
      'border-[color:var(--cs-brand-accent,var(--cs-text-primary))] bg-[color:var(--cs-brand-action,var(--cs-text-primary))] font-medium text-[color:var(--cs-brand-foreground,var(--cs-surface-0))]';
  } else {
    // Today: the accent edge and a semibold numeral, so it reads at a glance (the faint
    // control-hover edge it had was easy to miss) — only when today is pickable (the caller
    // passes today=false for a disabled today). Disabled days drop to the tertiary ink on top of
    // the 60% disabled opacity and take no hover ground; they are also struck through unless
    // `strikeDisabled` is false (a grid where most days are disabled, where the strikes are noise).
    state = `${today ? 'border-[color:var(--cs-brand-accent,var(--cs-text-primary))] font-semibold' : 'border-transparent font-normal'} bg-transparent text-[color:var(--cs-text-primary)] hover:bg-[color:var(--cs-surface-1)] disabled:text-[color:var(--cs-text-tertiary)] ${strikeDisabled ? 'disabled:line-through ' : ''}disabled:hover:bg-transparent`;
  }
  return `inline-flex items-center justify-center box-border h-[var(--cs-size-button-sm)] w-[var(--cs-size-button-sm)] rounded-[var(--cs-control-radius)] border font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] ${state} ${FOCUS_AND_DISABLED}`;
}
